import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { bankName } from '../../../shared/config/localeProfile';
import { ArrowLeftIcon, GlobeIcon, TruckIcon, HelpCircleIcon, InfoIcon, MailIcon, CheckCircleIcon, PlusIcon, Trash2Icon, FacebookIcon, InstagramIcon, TwitterIcon, MusicIcon, CopyIcon, PhoneIcon, MapPinIcon, StoreIcon, UploadIcon, MessageCircleIcon, PackageIcon, PercentIcon, UserIcon, DollarSignIcon, BellIcon, MessageSquareIcon, ScrollTextIcon, SparklesIcon } from 'lucide-react';
type ShippingMode = 'none' | 'free' | 'flat' | 'calculator';
interface WeightTier {
  id: string;
  maxWeightKg: number;
  pricePerKg: number;
}
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
export function ShopSettings() {
  const navigate = useNavigate();
  const {
    shopId
  } = useParams<{
    shopId: string;
  }>();
  const [searchParams] = useSearchParams();
  const initialSection = searchParams.get('section') || 'storefront';
  const [activeSection, setActiveSection] = useState(initialSection);
  // Domain (default derived from the active brand)
  const [customDomain, setCustomDomain] = useState(`shop.${bankName().toLowerCase().replace(/\s+/g, '')}.com.my`);
  const [domainVerified, setDomainVerified] = useState(false);
  // Shipping
  const [shippingMode, setShippingMode] = useState<ShippingMode>('calculator');
  const [flatRate, setFlatRate] = useState('5.00');
  const [baseFee, setBaseFee] = useState('2.50');
  const [weightTiers, setWeightTiers] = useState<WeightTier[]>([{
    id: 't1',
    maxWeightKg: 1,
    pricePerKg: 3
  }, {
    id: 't2',
    maxWeightKg: 5,
    pricePerKg: 2
  }, {
    id: 't3',
    maxWeightKg: 20,
    pricePerKg: 1.25
  }]);
  const [zones, setZones] = useState<{
    id: string;
    name: string;
    surcharge: number;
  }[]>([{
    id: 'z1',
    name: 'Kuala Lumpur',
    surcharge: 0
  }, {
    id: 'z2',
    name: 'Selangor',
    surcharge: 1.5
  }, {
    id: 'z3',
    name: 'East Coast',
    surcharge: 8
  }, {
    id: 'z4',
    name: 'East Malaysia',
    surcharge: 10
  }]);
  // FAQs
  const [faqs, setFaqs] = useState<FaqItem[]>([{
    id: 'f1',
    question: 'How long does shipping take?',
    answer: '1-2 business days within Kuala Lumpur. 3-5 days to other states.'
  }, {
    id: 'f2',
    question: 'What is your return policy?',
    answer: '14-day no-questions-asked returns on unworn items.'
  }]);
  // About
  const [aboutText, setAboutText] = useState('Beach Shop is your home for quality beachwear and accessories in Malaysia. Family-owned and operated since 2018.');
  // Contact
  const [contactEmail, setContactEmail] = useState('hello@beachshop.my');
  const [contactPhone, setContactPhone] = useState('+60 3 2333 4455');
  const [contactAddress, setContactAddress] = useState('Jalan Sultan Ismail, Kuala Lumpur 50250, Malaysia');
  // Socials
  const [facebook, setFacebook] = useState('https://facebook.com/beachshop');
  const [instagram, setInstagram] = useState('https://instagram.com/beachshop');
  const [twitter, setTwitter] = useState('');
  const [tiktok, setTiktok] = useState('https://tiktok.com/@beachshop');
  const [whatsapp, setWhatsapp] = useState('https://wa.me/9601234567');
  // Migrated Settings
  const [inventoryTracking, setInventoryTracking] = useState(true);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [notifyOnLowStock, setNotifyOnLowStock] = useState(false);
  const [allowBackorders, setAllowBackorders] = useState(false);
  const [hideOutOfStock, setHideOutOfStock] = useState(false);
  const [enableTaxCalculation, setEnableTaxCalculation] = useState(false);
  const [pricesIncludeTax, setPricesIncludeTax] = useState(false);
  const [taxRates, setTaxRates] = useState([{
    id: 'tx1',
    name: 'Standard Rate',
    rate: 6,
    isDefault: true
  }, {
    id: 'tx2',
    name: 'Reduced Rate',
    rate: 3,
    isDefault: false
  }, {
    id: 'tx3',
    name: 'Zero Rate',
    rate: 0,
    isDefault: false
  }]);
  const [customerFields, setCustomerFields] = useState([{
    id: 'cf1',
    name: 'Full Name',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: 'cf2',
    name: 'Email',
    type: 'Email',
    required: true,
    enabled: true
  }, {
    id: 'cf3',
    name: 'Phone Number',
    type: 'Phone',
    required: true,
    enabled: true
  }, {
    id: 'cf4',
    name: 'Address',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: 'cf5',
    name: 'Additional Notes',
    type: 'Textarea',
    required: false,
    enabled: true
  }]);
  const [shopFees, setShopFees] = useState([{
    id: 'f1',
    name: 'Processing Fee',
    amount: 2.5,
    type: 'Percentage',
    condition: 'Applied to all orders'
  }, {
    id: 'f2',
    name: 'Shipping Fee',
    amount: 5.0,
    type: 'Fixed',
    condition: 'Applied to orders over MYR 100'
  }]);
  const [confirmationMessage, setConfirmationMessage] = useState('Thank you for your order! We will process it shortly.');
  const [notificationEmail, setNotificationEmail] = useState('notifications@beachshop.mv');
  const [notificationPhone, setNotificationPhone] = useState('+960 333 4455');
  const [sendOrderNotifications, setSendOrderNotifications] = useState(true);
  const [sendLowStockAlerts, setSendLowStockAlerts] = useState(false);
  const [customerMessage, setCustomerMessage] = useState('Welcome to Beach Shop! Let us know if you have any questions.');
  const [enableCustomerMessages, setEnableCustomerMessages] = useState(true);
  // Inbox state (Customer Messages section)
  const [inboxSearch, setInboxSearch] = useState('');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>('c1');
  const [newMessageText, setNewMessageText] = useState('');
  const [conversations, setConversations] = useState<{
    id: string;
    customerName: string;
    unread: number;
    messages: {
      id: string;
      sender: 'customer' | 'shop';
      text: string;
      time: string;
    }[];
  }[]>([{
    id: 'c1',
    customerName: 'Aishath Saeed',
    unread: 2,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Hi! Do you ship the blue beach towel to Addu?',
      time: '2h ago'
    }, {
      id: 'm2',
      sender: 'customer',
      text: 'And how long would it take?',
      time: '2h ago'
    }]
  }, {
    id: 'c2',
    customerName: 'Ibrahim Waheed',
    unread: 0,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Can I exchange the swim shorts for a different size?',
      time: '1d ago'
    }, {
      id: 'm2',
      sender: 'shop',
      text: 'Yes — within 14 days of delivery, unworn. Bring the receipt.',
      time: '1d ago'
    }, {
      id: 'm3',
      sender: 'customer',
      text: 'Perfect, thanks!',
      time: '1d ago'
    }]
  }, {
    id: 'c3',
    customerName: 'Hassan Manik',
    unread: 1,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'When will the Beach Towel be back in stock?',
      time: '3d ago'
    }]
  }, {
    id: 'c4',
    customerName: 'Fathimath Nadhiya',
    unread: 0,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Are the sunglasses polarized?',
      time: '5d ago'
    }, {
      id: 'm2',
      sender: 'shop',
      text: 'Yes, all our sunglasses are polarized and 100% UV protected.',
      time: '5d ago'
    }]
  }, {
    id: 'c5',
    customerName: 'Mariyam Zoona',
    unread: 0,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Do you do gift wrapping?',
      time: '1w ago'
    }, {
      id: 'm2',
      sender: 'shop',
      text: 'We do! It’s MYR 30 per item — selectable at checkout.',
      time: '1w ago'
    }]
  }, {
    id: 'c6',
    customerName: 'Mohamed Rasheed',
    unread: 0,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Can I pick up my order from the Kuala Lumpur store?',
      time: '2w ago'
    }, {
      id: 'm2',
      sender: 'shop',
      text: 'Absolutely — local pickup is free, just select it at checkout.',
      time: '2w ago'
    }]
  }]);
  // Legal
  const [privacyPolicy, setPrivacyPolicy] = useState('We respect your privacy. Any personal information you share with Beach Shop — such as your name, contact details, and order history — is used solely to fulfill your orders and improve your shopping experience. We do not sell or share your information with third parties, except where required to deliver your purchase (e.g. shipping providers).');
  const [termsOfService, setTermsOfService] = useState('By placing an order with Beach Shop, you agree to the following terms: All sales are subject to product availability. Prices are listed in MYR and inclusive of applicable taxes. Returns are accepted within 14 days of delivery for unworn items in original packaging. Beach Shop reserves the right to refuse service at our discretion.');
  // Widgets
  const [showFreeShippingWidget, setShowFreeShippingWidget] = useState(true);
  const [showEasyReturnsWidget, setShowEasyReturnsWidget] = useState(true);
  const [showSecureCheckoutWidget, setShowSecureCheckoutWidget] = useState(true);
  const [showLocalShopWidget, setShowLocalShopWidget] = useState(false);
  // Free shipping threshold (pre-tax amount in MYR; 0 disables)
  const [freeShippingThreshold, setFreeShippingThreshold] = useState('500');
  // Express shipping add-on (% surcharge on top of base shipping cost)
  const [expressShippingEnabled, setExpressShippingEnabled] = useState(false);
  const [expressShippingPercent, setExpressShippingPercent] = useState('25');
  const [savedToast, setSavedToast] = useState(false);
  // Storefront
  const [heroImage, setHeroImage] = useState('https://images.unsplash.com/photo-1514282401047-d79a71a590e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80');
  const [shopTitle, setShopTitle] = useState('Beach Shop');
  const [tagline, setTagline] = useState('Quality beachwear and accessories');
  const [description, setStoreDescription] = useState('Your home for quality beachwear in Malaysia.');
  const addTier = () => {
    const maxWeight = weightTiers[weightTiers.length - 1]?.maxWeightKg ?? 0;
    setWeightTiers([...weightTiers, {
      id: 't' + Math.random().toString(36).slice(2, 7),
      maxWeightKg: maxWeight + 5,
      pricePerKg: 1
    }]);
  };
  const removeTier = (id: string) => setWeightTiers(weightTiers.filter(t => t.id !== id));
  const addZone = () => {
    setZones([...zones, {
      id: 'z' + Math.random().toString(36).slice(2, 7),
      name: 'New zone',
      surcharge: 0
    }]);
  };
  const removeZone = (id: string) => setZones(zones.filter(z => z.id !== id));
  const addFaq = () => setFaqs([...faqs, {
    id: 'f' + Math.random().toString(36).slice(2, 7),
    question: '',
    answer: ''
  }]);
  const removeFaq = (id: string) => setFaqs(faqs.filter(f => f.id !== id));
  const handleSave = () => {
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2000);
  };
  const sections = [{
    id: 'storefront',
    label: 'Storefront',
    icon: StoreIcon
  }, {
    id: 'domain',
    label: 'Custom domain',
    icon: GlobeIcon
  }, {
    id: 'shipping',
    label: 'Shipping',
    icon: TruckIcon
  }, {
    id: 'inventory',
    label: 'Inventory',
    icon: PackageIcon
  }, {
    id: 'tax',
    label: 'Tax',
    icon: PercentIcon
  }, {
    id: 'customer-fields',
    label: 'Customer fields',
    icon: UserIcon
  }, {
    id: 'fees',
    label: 'Fees & charges',
    icon: DollarSignIcon
  }, {
    id: 'notifications',
    label: 'Order notifications',
    icon: BellIcon
  }, {
    id: 'messages',
    label: 'Customer messages',
    icon: MessageSquareIcon
  }, {
    id: 'faq',
    label: 'FAQ',
    icon: HelpCircleIcon
  }, {
    id: 'about',
    label: 'About',
    icon: InfoIcon
  }, {
    id: 'contact',
    label: 'Contact',
    icon: MailIcon
  }, {
    id: 'social',
    label: 'Social media',
    icon: InstagramIcon
  }, {
    id: 'widgets',
    label: 'Widgets',
    icon: SparklesIcon
  }, {
    id: 'legal',
    label: 'Legal',
    icon: ScrollTextIcon
  }];
  return <div className="max-w-[1200px] mx-auto space-y-6" data-id="element-13196" data-mp="2mclfc7ss0fqn">
      {/* Header */}
      <div data-id="element-13197" data-mp="2meog9mvgvta5">
        <button onClick={() => navigate(shopId ? `/shop-details?id=${shopId}` : '/shops')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-3" data-id="element-13198" data-mp="2mexw4hmssk24">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-13199" data-mp="2hld3z67vykau" />
          Back to shop
        </button>
        <div className="flex items-center justify-between" data-id="element-13200" data-mp="35axqfz38zzov">
          <div data-id="element-13201" data-mp="359cuq6576apf">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-13202" data-mp="3a25zgu0o1j1i">
              Shop settings · {shopTitle}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-13203" data-mp="1medxnjmy72oi">
              Configure storefront, shipping, inventory and more for this shop.
            </p>
          </div>
        </div>
      </div>

      {savedToast && <div className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm shadow-lg flex items-center gap-2 z-50" data-id="element-13204" data-mp="1cgpymhib790j">
          <CheckCircleIcon size={16} className="text-green-400" data-id="element-13205" data-mp="18g60pbpww1dc" />
          Settings saved
        </div>}

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-13206" data-mp="20598v2kx5vv9">
        {/* Left Column - Navigation */}
        <div className="lg:w-[260px] flex-shrink-0" data-id="element-13207" data-mp="1vej3vqgenv13">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-6" data-id="element-13208" data-mp="1vd7zbof4yj8x">
            <div className="p-4 border-b border-gray-100 flex items-center gap-3" data-id="element-13209" data-mp="1rc5zb5dea35w">
              <div className="w-10 h-10 rounded-full bg-[#FEE7E5] text-[#9F140F] flex items-center justify-center font-bold text-lg" data-id="element-13210" data-mp="2nvvg4rn3nc47">
                {shopTitle.charAt(0)}
              </div>
              <div className="flex-1 min-w-0" data-id="element-13211" data-mp="5m91ea6sidpg">
                <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-13212" data-mp="5nk56a08jlgk">
                  {shopTitle}
                </p>
                <button onClick={() => navigate('/shops')} className="text-xs text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-13213" data-mp="emfaljvggrit">
                  Switch shop
                </button>
              </div>
            </div>
            <nav className="p-2 space-y-0.5 max-h-[calc(100vh-200px)] overflow-y-auto" data-id="element-13214" data-mp="oi8u5n7ldla3">
              {sections.map(section => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return <button key={section.id} onClick={() => setActiveSection(section.id)} className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-[#FEE7E5] text-[#9F140F]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-13215" data-mp="12agdac5yddqb">
                    <Icon size={16} className={isActive ? 'text-[#E2231A]' : 'text-gray-400'} data-id="element-13216" data-mp="33ehgssnkimgu" />
                    {section.label}
                  </button>;
            })}
            </nav>
          </div>
        </div>

        {/* Right Column - Content */}
        <div className="flex-1 min-w-0" data-id="element-13217" data-mp="3r1z3wavw0wdh">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-start gap-2" data-id="element-13218" data-mp="3n1yooo0ysh5d">
            <InfoIcon size={16} className="text-blue-600 mt-0.5 flex-shrink-0" data-id="element-13219" data-mp="4w3hraqt5b68" />
            <p className="text-sm text-blue-800" data-id="element-13220" data-mp="3w2dd3w986cd3">
              These settings apply only to <strong data-id="element-13221" data-mp="26ngkpu97t8zn">{shopTitle}</strong>.
            </p>
          </div>

          {/* Storefront */}
          {activeSection === 'storefront' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13222" data-mp="21qgg7ns0kean">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13223" data-mp="h738htwo4xw7">
                <StoreIcon size={16} className="text-gray-500" data-id="element-13224" data-mp="m4cv71soavgc" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13225" data-mp="7hbasqt9voni">
                  Storefront branding
                </h2>
              </div>
              <div className="px-6 py-5 space-y-6" data-id="element-13226" data-mp="cea08w1zr7fd">
                <div data-id="element-13227" data-mp="3uky82tofe8oq">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13228" data-mp="3ukfzqyoho40g">
                    Hero Image
                  </label>
                  <div className="relative aspect-[21/9] w-full max-w-3xl rounded-xl border-2 border-dashed border-gray-300 overflow-hidden bg-gray-50 hover:bg-gray-100 transition-colors group cursor-pointer" data-id="element-13229" data-mp="3knjq2pmutbub">
                    {heroImage ? <>
                        <img src={heroImage} alt="Hero" className="w-full h-full object-cover" data-id="element-13230" data-mp="1itubeapa7zwa" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-13231" data-mp="bnr981v70819">
                          <div className="bg-white px-4 py-2 rounded-lg text-sm font-medium text-gray-900 flex items-center gap-2 shadow-sm" data-id="element-13232" data-mp="1utkvf3irouq">
                            <UploadIcon size={16} data-id="element-13233" data-mp="3t0br98muhqka" />
                            Change image
                          </div>
                        </div>
                      </> : <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500" data-id="element-13234" data-mp="3k3uclvf9vajs">
                        <UploadIcon size={24} className="mb-2 text-gray-400" data-id="element-13235" data-mp="3a3jzsbx5g555" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-13236" data-mp="3a5mzxos0nenl">
                          Click to upload
                        </span>
                        <span className="text-xs mt-1" data-id="element-13237" data-mp="18cytl6k2sb3l">
                          Recommended 1600×600px, JPG or PNG
                        </span>
                      </div>}
                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={e => {
                  if (e.target.files?.[0]) {
                    setHeroImage(URL.createObjectURL(e.target.files[0]));
                  }
                }} data-id="element-13238" data-mp="1ktrj658uawn8" />
                  </div>
                  {heroImage && <button onClick={() => setHeroImage('')} className="mt-2 text-xs text-red-600 hover:text-red-700 font-medium" data-id="element-13239" data-mp="16v1m2pm0swyv">
                      Remove image
                    </button>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13240" data-mp="2n8i9izdziqfh">
                  <div data-id="element-13241" data-mp="2nauqb980auqy">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13242" data-mp="1igw92cor8j2">
                      Shop Title
                    </label>
                    <input type="text" value={shopTitle} onChange={e => setShopTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13243" data-mp="6ep7i4nrihky" />
                  </div>
                  <div data-id="element-13244" data-mp="3jo3n6evfat2s">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13245" data-mp="3jpo9jh7l35pk">
                      Tagline
                    </label>
                    <input type="text" value={tagline} onChange={e => setTagline(e.target.value)} placeholder="e.g. Quality beachwear and accessories" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13246" data-mp="3sptezlt7vu13" />
                  </div>
                  <div className="md:col-span-2" data-id="element-13247" data-mp="18c4y14up50rb">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13248" data-mp="18attv3xc9qzo">
                      Description
                    </label>
                    <textarea value={description} onChange={e => setStoreDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13249" data-mp="zbzrsy18o8eq" />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13250" data-mp="2jqa5i7u4bsre">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13251" data-mp="2fqrj17mnf9mg">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Custom domain */}
          {activeSection === 'domain' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13252" data-mp="84zt4a8cxg72">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13253" data-mp="vtalsq3wg9k9">
                <GlobeIcon size={16} className="text-gray-500" data-id="element-13254" data-mp="10svonbm6x229" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13255" data-mp="10q9h3bzaumk1">
                  Custom domain
                </h2>
              </div>
              <div className="px-6 py-5 space-y-4" data-id="element-13256" data-mp="rr5uvz4u5r1b">
                <p className="text-sm text-gray-500" data-id="element-13257" data-mp="rts4037dkeln">
                  Link your own domain so customers see your branded URL instead
                  of a default one.
                </p>
                <div className="flex flex-col sm:flex-row gap-3" data-id="element-13258" data-mp="3cg3fp5rlbn5u">
                  <div className="flex-1" data-id="element-13259" data-mp="33zunnbrba78v">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13260" data-mp="3430aslgdqz1y">
                      Your domain
                    </label>
                    <div className="flex" data-id="element-13261" data-mp="3dyk7ha2rq1rr">
                      <span className="inline-flex items-center px-3 bg-gray-50 border border-r-0 border-gray-200 rounded-l-lg text-sm text-gray-500" data-id="element-13262" data-mp="390ig81v6uw57">
                        https://
                      </span>
                      <input type="text" value={customDomain} onChange={e => setCustomDomain(e.target.value)} placeholder="shop.yourdomain.com" className="flex-1 px-3 py-2 border border-gray-200 rounded-r-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13263" data-mp="2gccsd68u8r15" />
                    </div>
                  </div>
                  <div className="flex items-end" data-id="element-13264" data-mp="10toxnr5c5jtw">
                    <button onClick={() => setDomainVerified(true)} className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-13265" data-mp="10uzwrdnu63eo">
                      Verify domain
                    </button>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-13266" data-mp="1jkgch883e2fo">
                  <p className="text-xs font-semibold text-blue-900 mb-2" data-id="element-13267" data-mp="1jmkibvpjxbil">
                    DNS configuration
                  </p>
                  <p className="text-xs text-blue-800 mb-2" data-id="element-13268" data-mp="1ti4aq84wps2l">
                    Add this CNAME record at your domain provider:
                  </p>
                  <div className="flex items-center gap-2 bg-white border border-blue-200 rounded px-3 py-2 font-mono text-xs" data-id="element-13269" data-mp="1xhv5emxk0sce">
                    <span className="text-gray-500" data-id="element-13270" data-mp="35oiiqvasn49">CNAME</span>
                    <span className="text-gray-900" data-id="element-13271" data-mp="380ywzleqpf7">
                      {customDomain || 'shop'}
                    </span>
                    <span className="text-gray-400" data-id="element-13272" data-mp="3n1338i4uouau">→</span>
                    <span className="text-gray-900 flex-1" data-id="element-13273" data-mp="3mzjmkqfa8h8j">
                      shops.bilabila.app
                    </span>
                    <button onClick={() => navigator.clipboard?.writeText('shops.bilabila.app').catch(() => {})} className="text-gray-400 hover:text-gray-700" aria-label="Copy target" data-id="element-13274" data-mp="3hznnn5lfzfzu">
                      <CopyIcon size={12} data-id="element-13275" data-mp="5is37wm914ah" />
                    </button>
                  </div>
                </div>
                {domainVerified && <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-13276" data-mp="2qbdgs06mjiso">
                    <CheckCircleIcon size={16} data-id="element-13277" data-mp="2lc0qt6kghvlv" />
                    Domain verified. SSL certificate will be issued
                    automatically.
                  </div>}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13278" data-mp="2rsj3808pkhld">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13279" data-mp="2dsri7fjry0xv">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Shipping */}
          {activeSection === 'shipping' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13280" data-mp="1u4r430q3b6b5">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13281" data-mp="1z32cr84nfars">
                <TruckIcon size={16} className="text-gray-500" data-id="element-13282" data-mp="1l3koquz6g1jh" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13283" data-mp="1l4cg1tlqrd3y">
                  Shipping
                </h2>
              </div>
              <div className="px-6 py-5 space-y-5" data-id="element-13284" data-mp="16glvcugrex24">
                {/* Mode picker */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-13285" data-mp="11hr3z44q806e">
                  {([{
                id: 'none' as const,
                title: 'No shipping',
                desc: 'Local pickup only.'
              }, {
                id: 'free' as const,
                title: 'Free shipping',
                desc: 'No shipping fee on any order.'
              }, {
                id: 'flat' as const,
                title: 'Flat rate',
                desc: 'Same shipping fee on every order.'
              }, {
                id: 'calculator' as const,
                title: 'Calculator',
                desc: 'Cost based on weight + delivery zone.'
              }] as const).map(opt => <button key={opt.id} onClick={() => setShippingMode(opt.id)} className={`text-left p-4 rounded-lg border-2 transition-colors ${shippingMode === opt.id ? 'border-[#E2231A] bg-red-50/40' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-13286" data-mp="3fi5bom7oppcd">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-13287" data-mp="2runf8ddh6332">
                        {opt.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-13288" data-mp="2vsbbbjqwm1wj">{opt.desc}</p>
                    </button>)}
                </div>

                {shippingMode === 'flat' && <div className="max-w-sm" data-id="element-13289" data-mp="35mbsijzgmxlm">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13290" data-mp="2841xp53ugkgh">
                      Flat shipping rate (MYR)
                    </label>
                    <input type="number" value={flatRate} onChange={e => setFlatRate(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13291" data-mp="25kx5l442lkip" />
                  </div>}

                {shippingMode === 'calculator' && <div className="space-y-5" data-id="element-13292" data-mp="1mn40pw9vz0io">
                    <div className="max-w-sm" data-id="element-13293" data-mp="1hukob2mxqbfg">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13294" data-mp="1hthvuq9ys0xf">
                        Base handling fee (MYR)
                      </label>
                      <input type="number" value={baseFee} onChange={e => setBaseFee(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13295" data-mp="17yhmxyzwqgtk" />
                    </div>

                    {/* Weight tiers */}
                    <div data-id="element-13296" data-mp="2xf21fu8ud0j">
                      <div className="flex items-center justify-between mb-2" data-id="element-13297" data-mp="hsbk0kruueqt">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-13298" data-mp="hq8tlzmsijlk">
                          Weight-based pricing
                        </p>
                        <button onClick={addTier} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-13299" data-mp="3lad884hmq235">
                          <PlusIcon size={14} data-id="element-13300" data-mp="3v0g0g2gxfc0f" />
                          Add tier
                        </button>
                      </div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-13301" data-mp="32iao2j5s43o5">
                        <table className="w-full text-sm" data-id="element-13302" data-mp="2xjzdg67ntp4v">
                          <thead className="bg-gray-50 text-xs uppercase text-gray-500" data-id="element-13303" data-mp="2xjh9enera6me">
                            <tr data-id="element-13304" data-mp="1d042gvr8iu90">
                              <th className="text-left px-4 py-2 font-medium" data-id="element-13305" data-mp="1czvpz4z0xqyc">
                                Up to weight (kg)
                              </th>
                              <th className="text-left px-4 py-2 font-medium" data-id="element-13306" data-mp="1353rlyxmb02i">
                                Price per kg (MYR)
                              </th>
                              <th className="w-10" data-id="element-13307" data-mp="1vrwuamuki9f5"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100" data-id="element-13308" data-mp="20pydd7rzu8qm">
                            {weightTiers.map(tier => <tr key={tier.id} data-id="element-13309" data-mp="1mtkt5qw2rt8g">
                                <td className="px-4 py-2" data-id="element-13310" data-mp="1qsbts7zr1t0b">
                                  <input type="number" value={tier.maxWeightKg} onChange={e => setWeightTiers(weightTiers.map(t => t.id === tier.id ? {
                            ...t,
                            maxWeightKg: Number(e.target.value)
                          } : t))} className="w-24 px-2 py-1 border border-gray-200 rounded text-sm" data-id="element-13311" data-mp="1quy2iaydmek4" />
                                </td>
                                <td className="px-4 py-2" data-id="element-13312" data-mp="3jonsxpeifh00">
                                  <input type="number" step="0.25" value={tier.pricePerKg} onChange={e => setWeightTiers(weightTiers.map(t => t.id === tier.id ? {
                            ...t,
                            pricePerKg: Number(e.target.value)
                          } : t))} className="w-24 px-2 py-1 border border-gray-200 rounded text-sm" data-id="element-13313" data-mp="3er4989z1mc0o" />
                                </td>
                                <td className="px-4 py-2 text-right" data-id="element-13314" data-mp="26ezo8rjx7di4">
                                  <button onClick={() => removeTier(tier.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" aria-label="Remove tier" data-id="element-13315" data-mp="26hcctnk6om45">
                                    <Trash2Icon size={14} data-id="element-13316" data-mp="2pdu300jdwdya" />
                                  </button>
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Zones */}
                    <div data-id="element-13317" data-mp="2u5vlw5b252xa">
                      <div className="flex items-center justify-between mb-2" data-id="element-13318" data-mp="384v0ek4uw1fq">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-13319" data-mp="382r00c661kk7">
                          Delivery zones
                        </p>
                        <button onClick={addZone} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-13320" data-mp="3mraihms4pfs1">
                          <PlusIcon size={14} data-id="element-13321" data-mp="3csuqlhla22hh" />
                          Add zone
                        </button>
                      </div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-13322" data-mp="3412llmf0vlka">
                        <table className="w-full text-sm" data-id="element-13323" data-mp="33z7wrx0mr9ax">
                          <thead className="bg-gray-50 text-xs uppercase text-gray-500" data-id="element-13324" data-mp="38skl061ede35">
                            <tr data-id="element-13325" data-mp="2u3xy9j9fxk1w">
                              <th className="text-left px-4 py-2 font-medium" data-id="element-13326" data-mp="2u0s6fw9fg42h">
                                Zone name
                              </th>
                              <th className="text-left px-4 py-2 font-medium" data-id="element-13327" data-mp="2l39gcdgxezjq">
                                Surcharge (MYR)
                              </th>
                              <th className="w-10" data-id="element-13328" data-mp="vl2s0iazzc0w"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100" data-id="element-13329" data-mp="qtui7l5r9on7">
                            {zones.map(zone => <tr key={zone.id} data-id="element-13330" data-mp="lw1f50zr1ar5">
                                <td className="px-4 py-2" data-id="element-13331" data-mp="gw6omyjvyn6m">
                                  <input type="text" value={zone.name} onChange={e => setZones(zones.map(z => z.id === zone.id ? {
                            ...z,
                            name: e.target.value
                          } : z))} className="w-full px-2 py-1 border border-gray-200 rounded text-sm" data-id="element-13332" data-mp="gxrielydm23f" />
                                </td>
                                <td className="px-4 py-2" data-id="element-13333" data-mp="29r7lkkkunbhb">
                                  <input type="number" value={zone.surcharge} onChange={e => setZones(zones.map(z => z.id === zone.id ? {
                            ...z,
                            surcharge: Number(e.target.value)
                          } : z))} className="w-32 px-2 py-1 border border-gray-200 rounded text-sm" data-id="element-13334" data-mp="2ekivwvoveadz" />
                                </td>
                                <td className="px-4 py-2 text-right" data-id="element-13335" data-mp="39v71dzyk7u5u">
                                  <button onClick={() => removeZone(zone.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" aria-label="Remove zone" data-id="element-13336" data-mp="2m3s1sey63qya">
                                    <Trash2Icon size={14} data-id="element-13337" data-mp="2r4plivytst1w" />
                                  </button>
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>}

                {(shippingMode === 'flat' || shippingMode === 'calculator') && <div className="border-t border-gray-100 pt-5" data-id="element-13338" data-mp="1f9udiyubm9p3">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13339" data-mp="1k85ml7cskg69">
                      Free shipping threshold (pre-tax, MYR)
                    </label>
                    <div className="flex items-center gap-2 max-w-sm" data-id="element-13340" data-mp="2bzbp1ufzipyp">
                      <span className="text-sm text-gray-500" data-id="element-13341" data-mp="2byjrwf6qok6b">MYR</span>
                      <input type="number" min="0" step="0.01" value={freeShippingThreshold} onChange={e => setFreeShippingThreshold(e.target.value)} placeholder="0.00" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13342" data-mp="1y75agcshcr1d" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5" data-id="element-13343" data-mp="2cgizurl9s1yd">
                      Orders with a pre-tax subtotal at or above this amount
                      qualify for free shipping. Set to 0 to disable.
                    </p>
                  </div>}

                {(shippingMode === 'flat' || shippingMode === 'calculator') && <div className="border-t border-gray-100 pt-5" data-id="element-13344" data-mp="398e6fw1m5ufj">
                    <div className="flex items-center justify-between mb-2" data-id="element-13345" data-mp="2ve3pi0vuz4k1">
                      <div data-id="element-13346" data-mp="2zfyrz8piznze">
                        <p className="text-sm font-medium text-gray-900" data-id="element-13347" data-mp="2zf5ocglv5elh">
                          Express shipping add-on (optional)
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-13348" data-mp="3rxb2o5gkmx0e">
                          Let customers upgrade to express delivery for a
                          percentage surcharge on the shipping fee.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-13349" data-mp="1bgv89k4j3px9">
                        <input type="checkbox" className="sr-only peer" checked={expressShippingEnabled} onChange={e => setExpressShippingEnabled(e.target.checked)} data-id="element-13350" data-mp="1q9oml282e00c" />
                        <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13351" data-mp="17hdzagpw8anc"></div>
                      </label>
                    </div>
                    {expressShippingEnabled && <div className="max-w-sm mt-3" data-id="element-13352" data-mp="2l0mslqq7sfpa">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13353" data-mp="2l04oy9182z7c">
                          Express surcharge
                        </label>
                        <div className="flex items-center gap-2" data-id="element-13354" data-mp="2uz3mowkg9afk">
                          <input type="number" min="0" step="1" value={expressShippingPercent} onChange={e => setExpressShippingPercent(e.target.value)} placeholder="25" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13355" data-mp="2zxx2ivn7ldiw" />
                          <span className="text-sm text-gray-500" data-id="element-13356" data-mp="1oi8d7xmhg2ex">%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5" data-id="element-13357" data-mp="5nvulokj2ki2">
                          Applied as a percentage on top of the calculated
                          shipping cost when the customer selects Express at
                          checkout.
                        </p>
                      </div>}
                  </div>}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13358" data-mp="3itnla6xlo342">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13359" data-mp="3e3fjyci2viru">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Inventory */}
          {activeSection === 'inventory' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13360" data-mp="2v4l5z5qfumcq">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13361" data-mp="z16pc4q3y2y2">
                <PackageIcon size={16} className="text-gray-500" data-id="element-13362" data-mp="1409sfjmj1z3e" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13363" data-mp="13zq67qpugojk">
                  Inventory Management
                </h2>
              </div>
              <div className="px-6 py-5 space-y-6" data-id="element-13364" data-mp="ua6myjy7yso6">
                <div className="flex items-center justify-between" data-id="element-13365" data-mp="u8n9t27v0xqm">
                  <div data-id="element-13366" data-mp="g9njlq4p8asc">
                    <p className="text-sm font-medium text-gray-900" data-id="element-13367" data-mp="g944eh6fo0i0">
                      Enable Stock Management
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-13368" data-mp="6ciyzuqi4ndn">
                      Track inventory levels for products in this shop
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-13369" data-mp="bdos3to2qcnp">
                    <input type="checkbox" className="sr-only peer" checked={inventoryTracking} onChange={e => setInventoryTracking(e.target.checked)} data-id="element-13370" data-mp="20rbnzawvqfwm" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13371" data-mp="3l9cjhmksyavs"></div>
                  </label>
                </div>

                <div data-id="element-13372" data-mp="crg9ueu0uq5s">
                  <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-13373" data-mp="cuufpx1w9hhz">
                    Low Stock Threshold
                  </label>
                  <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={lowStockThreshold} onChange={e => setLowStockThreshold(parseInt(e.target.value))} data-id="element-13374" data-mp="mr77uqhsjnam" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-13375" data-mp="ngukby9kor75">
                    Products with stock below this number will be marked as low
                    stock
                  </p>
                </div>

                <div className="flex items-center justify-between" data-id="element-13376" data-mp="wfpo73p59p7a">
                  <div data-id="element-13377" data-mp="11flw3e0yq0s4">
                    <p className="text-sm font-medium text-gray-900" data-id="element-13378" data-mp="11gwxx8aukygl">
                      Notify on Low Stock
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-13379" data-mp="16eoqm4jbgolu">
                      Receive notifications when products reach low stock
                      threshold
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-13380" data-mp="1g6uotw05w29h">
                    <input type="checkbox" className="sr-only peer" checked={notifyOnLowStock} onChange={e => setNotifyOnLowStock(e.target.checked)} data-id="element-13381" data-mp="1k5k4jv8o9vwa" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13382" data-mp="249hk8peq2wo5"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between" data-id="element-13383" data-mp="1qa004ncdg7l4">
                  <div data-id="element-13384" data-mp="2mzh3uwrsnjk6">
                    <p className="text-sm font-medium text-gray-900" data-id="element-13385" data-mp="2mxmjpkocjvh5">
                      Allow Backorders
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-13386" data-mp="2i1xpzxz0d8sr">
                      Allow customers to purchase products that are out of stock
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-13387" data-mp="297aqfig0zqof">
                    <input type="checkbox" className="sr-only peer" checked={allowBackorders} onChange={e => setAllowBackorders(e.target.checked)} data-id="element-13388" data-mp="35r0794pqczmq" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13389" data-mp="3pdzgbwqyv08e"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between" data-id="element-13390" data-mp="78w6xztyw226">
                  <div data-id="element-13391" data-mp="c6nxoqivli4s">
                    <p className="text-sm font-medium text-gray-900" data-id="element-13392" data-mp="c77hkcs36glg">
                      Hide Out of Stock Products
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-13393" data-mp="3th5j2ueve9a4">
                      Automatically hide products when they are out of stock
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-13394" data-mp="2hyckwlpqhe95">
                    <input type="checkbox" className="sr-only peer" checked={hideOutOfStock} onChange={e => setHideOutOfStock(e.target.checked)} data-id="element-13395" data-mp="2mzs6cevtpms2" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13396" data-mp="1e1sbyo6n9qik"></div>
                  </label>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13397" data-mp="hcuf90fc3iir">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13398" data-mp="v47xmb1iaac7">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Tax */}
          {activeSection === 'tax' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13399" data-mp="2rtfho3toeqs6">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13400" data-mp="2n1xgpfrnkq77">
                <PercentIcon size={16} className="text-gray-500" data-id="element-13401" data-mp="2j0ly3u77558i" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13402" data-mp="2iyrh2qz1px9p">
                  Tax Configuration
                </h2>
              </div>
              <div className="px-6 py-5 space-y-6" data-id="element-13403" data-mp="3asbbvf5jb560">
                <div className="flex items-center justify-between" data-id="element-13404" data-mp="3apxh77ernuyz">
                  <div data-id="element-13405" data-mp="36rrfidssc5kx">
                    <p className="text-sm font-medium text-gray-900" data-id="element-13406" data-mp="36oby34bn2kgc">
                      Enable Tax Calculation
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-13407" data-mp="2wss44ngmxvu8">
                      Calculate and apply taxes to orders
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-13408" data-mp="1qa3osbfvq7mn">
                    <input type="checkbox" className="sr-only peer" checked={enableTaxCalculation} onChange={e => setEnableTaxCalculation(e.target.checked)} data-id="element-13409" data-mp="1lbsfc1thxz4y" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13410" data-mp="1l5i0zoxwvm5h"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between" data-id="element-13411" data-mp="23xu9yuj2llol">
                  <div data-id="element-13412" data-mp="23ylzpopzkp6y">
                    <p className="text-sm font-medium text-gray-900" data-id="element-13413" data-mp="28wx4vm5c6biu">
                      Prices Include Tax
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-13414" data-mp="9kur8sje8bd3">
                      Product prices already include tax
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-13415" data-mp="6i85z7ct4yqv">
                    <input type="checkbox" className="sr-only peer" checked={pricesIncludeTax} onChange={e => setPricesIncludeTax(e.target.checked)} data-id="element-13416" data-mp="6k1cmmnf7iyv" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13417" data-mp="bgi1jj2g21d0"></div>
                  </label>
                </div>

                <div data-id="element-13418" data-mp="z63xlxnis0gs">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-13419" data-mp="z8q4dv2l6bxy">
                    Tax Rates
                  </h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-13420" data-mp="18725ir12ws4n">
                    <table className="w-full" data-id="element-13421" data-mp="1468idzvgnhct">
                      <thead className="bg-gray-50 border-b border-gray-200" data-id="element-13422" data-mp="147jjtt1fod0r">
                        <tr data-id="element-13423" data-mp="1mvbwuaq9ga57">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13424" data-mp="1mv244fdtzovt">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13425" data-mp="1dsj6qaste4gy">
                            Rate (%)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13426" data-mp="1439d2qibodqq">
                            Default
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-13427" data-mp="18wcbbby1obd9">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white" data-id="element-13428" data-mp="l4naf775qekp">
                        {taxRates.map(rate => <tr key={rate.id} className="hover:bg-gray-50" data-id="element-13429" data-mp="q3gph423edmz">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-13430" data-mp="1sho1262rg570">
                              {rate.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-13431" data-mp="1jp49fpkvz0vo">
                              {rate.rate}%
                            </td>
                            <td className="px-4 py-3" data-id="element-13432" data-mp="1ep867sk0jdh6">
                              {rate.isDefault ? <span className="text-green-600 flex items-center gap-1" data-id="element-13433" data-mp="19shol0sk89tn">
                                  <CheckCircleIcon size={16} data-id="element-13434" data-mp="14r26nhloijfh" />
                                </span> : <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-13435" data-mp="10s31bvw1ckni">
                                  Set as default
                                </button>}
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-13436" data-mp="2q67vefb2dc6z">
                              <button className="text-sm text-blue-600 hover:text-blue-700 mr-3" data-id="element-13437" data-mp="2xoyzyniudoci">
                                Edit
                              </button>
                              <button className="text-sm text-red-600 hover:text-red-700" data-id="element-13438" data-mp="32lpeh6f00bvt">
                                Delete
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <button className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-13439" data-mp="2joeflmxa3kgw">
                    <PlusIcon size={16} data-id="element-13440" data-mp="2oqdkj18o2pfy" />
                    Add Tax Rate
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13441" data-mp="3yjwprv7jeg4">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13442" data-mp="7ylwtwlanzta">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Customer Fields */}
          {activeSection === 'customer-fields' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13443" data-mp="292wixflmkdfh">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13444" data-mp="1ld26v094qswa">
                <UserIcon size={16} className="text-gray-500" data-id="element-13445" data-mp="1hb8ah45ywb1x" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13446" data-mp="1v1tsyrrieiza">
                  Customer Information Fields
                </h2>
              </div>
              <div className="px-6 py-5" data-id="element-13447" data-mp="1qabty991qsgy">
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-13448" data-mp="12lj48uj0pgus">
                  <table className="w-full" data-id="element-13449" data-mp="12jg3bfgbu3ba">
                    <thead className="bg-gray-50 border-b border-gray-200" data-id="element-13450" data-mp="xn7t8ggzl0az">
                      <tr data-id="element-13451" data-mp="39a1mo6w89ry5">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13452" data-mp="398qiw72s8k71">
                          Field Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13453" data-mp="309d5x4fg9dhk">
                          Type
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-13454" data-mp="358g4q74u6nh3">
                          Required
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-13455" data-mp="soobx0ldxd7">
                          Enabled
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-13456" data-mp="3ny665d33u7hz">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white" data-id="element-13457" data-mp="2ckk5aunvuhgm">
                      {customerFields.map(field => <tr key={field.id} className="hover:bg-gray-50" data-id="element-13458" data-mp="348erxuc8lldy">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-13459" data-mp="2z77na45e52bi">
                            {field.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600" data-id="element-13460" data-mp="2pa2wts4iikow">
                            {field.type}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-13461" data-mp="3mx1y6g65ggt4">
                            <input type="checkbox" checked={field.required} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" readOnly data-id="element-13462" data-mp="3hxyv319qckns" />
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-13463" data-mp="9jzwrszacn74">
                            <input type="checkbox" checked={field.enabled} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" readOnly data-id="element-13464" data-mp="4rqemz4ewrfe" />
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-13465" data-mp="m3doziw69spk">
                            <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-13466" data-mp="r1fb6cowa859">
                              Edit
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <button className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-13467" data-mp="14sbybo01xx4x">
                  <PlusIcon size={16} data-id="element-13468" data-mp="1eqrh93oxe03c" />
                  Add Custom Field
                </button>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13469" data-mp="1jpui0bxvhk5i">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13470" data-mp="2i8yukty93mym">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Fees & Charges */}
          {activeSection === 'fees' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13471" data-mp="315rmalrrsfqu">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13472" data-mp="364bdb0l80hq2">
                <DollarSignIcon size={16} className="text-gray-500" data-id="element-13473" data-mp="3axdwqfwaaasj" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13474" data-mp="3fv5ys2qn7dag">
                  Custom Fees & Service Charges
                </h2>
              </div>
              <div className="px-6 py-5" data-id="element-13475" data-mp="3kwd9a9k66qkt">
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-4" data-id="element-13476" data-mp="3ovucsxg1ddnc">
                  <table className="w-full" data-id="element-13477" data-mp="1d8sarrbpg2us">
                    <thead className="bg-gray-50 border-b border-gray-200" data-id="element-13478" data-mp="1d9jt40gak434">
                      <tr data-id="element-13479" data-mp="196x9unxorrhj">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13480" data-mp="195lv5t2pczbn">
                          Fee Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13481" data-mp="z9slchgdbjxk">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13482" data-mp="1wyklxzqagxzr">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-13483" data-mp="1n5dhch3dhm0q">
                          Conditions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white" data-id="element-13484" data-mp="2fp28fmkj2xco">
                      {shopFees.map(fee => <tr key={fee.id} className="hover:bg-gray-50" data-id="element-13485" data-mp="8z8oyi59bslw">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-13486" data-mp="91375op596ma">
                            {fee.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900" data-id="element-13487" data-mp="3vdickp1d4wlm">
                            {fee.type === 'Percentage' ? `${fee.amount}%` : `MYR ${fee.amount.toFixed(2)}`}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600" data-id="element-13488" data-mp="3qfzy6j4ktp68">
                            {fee.type}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500" data-id="element-13489" data-mp="3cha2n7xenxjz">
                            {fee.condition}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-13490" data-mp="1fmdws846dofs">
                  <PlusIcon size={16} data-id="element-13491" data-mp="1ksj2sqrd0v1d" />
                  Add Fee
                </button>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13492" data-mp="1yk7dqqpvhdgr">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13493" data-mp="1tt7gfkcuctdq">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Order Notifications */}
          {activeSection === 'notifications' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13494" data-mp="2dgophj1z4u3u">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13495" data-mp="n3z82gf59m0v">
                <BellIcon size={16} className="text-gray-500" data-id="element-13496" data-mp="j36zzqi0dn66" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13497" data-mp="j507f80g4bf8">
                  Order Notifications
                </h2>
              </div>
              <div className="px-6 py-5 space-y-5" data-id="element-13498" data-mp="pie6adpcepbe">
                <div data-id="element-13499" data-mp="pla31ypgf7y9">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13500" data-mp="tl0pjq7y71wz">
                    Order Confirmation Message
                  </label>
                  <textarea className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" rows={3} value={confirmationMessage} onChange={e => setConfirmationMessage(e.target.value)} data-id="element-13501" data-mp="yhhntd8v2wns" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-13502" data-mp="fm1a996brzdh">
                    This message will be displayed to customers after they
                    complete an order
                  </p>
                </div>

                <div data-id="element-13503" data-mp="3j8i6tr6x9s84">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13504" data-mp="3j7qa2d1l9og9">
                    Notification Email
                  </label>
                  <input type="email" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" value={notificationEmail} onChange={e => setNotificationEmail(e.target.value)} data-id="element-13505" data-mp="36s0c5ohz19dl" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-13506" data-mp="2o194crdmtr1b">
                    Email address where order notifications will be sent
                  </p>
                </div>

                <div data-id="element-13507" data-mp="2izjs58eob7bn">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13508" data-mp="2e3bksh6pwif1">
                    Notification Phone Number
                  </label>
                  <input type="tel" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" value={notificationPhone} onChange={e => setNotificationPhone(e.target.value)} data-id="element-13509" data-mp="2524ytslaufee" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-13510" data-mp="1lhmx18sv7sd0">
                    Phone number where order notifications will be sent
                  </p>
                </div>

                <div className="space-y-3 pt-2" data-id="element-13511" data-mp="2e4qv0ttjj6fa">
                  <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3" data-id="element-13512" data-mp="2e73mbxl6ut50">
                    <div data-id="element-13513" data-mp="2j5wst6225jvo">
                      <p className="text-sm font-medium text-gray-900" data-id="element-13514" data-mp="24fphrbedr350">
                        Send Order Notifications
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-13515" data-mp="29cfxfxm9vwpw">
                        Receive notifications for new orders
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-13516" data-mp="31vd2aivq0mo8">
                      <input type="checkbox" className="sr-only peer" checked={sendOrderNotifications} onChange={e => setSendOrderNotifications(e.target.checked)} data-id="element-13517" data-mp="qekpnead4pcs" />
                      <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13518" data-mp="v8fgr2sas6lw"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3" data-id="element-13519" data-mp="bibtillrqs4l">
                    <div data-id="element-13520" data-mp="1rw29apm934u2">
                      <p className="text-sm font-medium text-gray-900" data-id="element-13521" data-mp="1rvk0yumbd05s">
                        Send Low Stock Alerts
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-13522" data-mp="1mzuzumunbb7d">
                        Receive notifications when products reach low stock
                        levels
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-13523" data-mp="2fzrs3gyxdjjw">
                      <input type="checkbox" className="sr-only peer" checked={sendLowStockAlerts} onChange={e => setSendLowStockAlerts(e.target.checked)} data-id="element-13524" data-mp="2kqroml44tzlv" />
                      <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13525" data-mp="2zpbyylvgchpa"></div>
                    </label>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13526" data-mp="11sbvjh3gcgdf">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13527" data-mp="11u6g2uata6gz">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Customer Messages */}
          {activeSection === 'messages' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col h-[640px]" data-id="element-13528" data-mp="1fscwsxnmpptf">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-13529" data-mp="1bq02d5ec1eby">
                <div className="flex items-center gap-2" data-id="element-13530" data-mp="16s7zxhg2a9ti">
                  <MessageSquareIcon size={16} className="text-gray-500" data-id="element-13531" data-mp="16trff5tw8gu8" />
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-13532" data-mp="cnvsmr6uhm3">
                    Customer Messages
                  </h2>
                </div>
                <div className="flex items-center gap-2" data-id="element-13533" data-mp="2os0ad70u11h0">
                  <span className="text-xs text-gray-500" data-id="element-13534" data-mp="2oqfgzkq97okq">
                    Accept new messages
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-13535" data-mp="2yi1n52nqixgm">
                    <input type="checkbox" className="sr-only peer" checked={enableCustomerMessages} onChange={e => setEnableCustomerMessages(e.target.checked)} data-id="element-13536" data-mp="2trl9pijwzbu9" />
                    <div className="w-9 h-5 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" data-id="element-13537" data-mp="3hhpdbpx3rbl9"></div>
                  </label>
                </div>
              </div>

              <div className="flex flex-1 min-h-0" data-id="element-13538" data-mp="1i5mkhp2l94ux">
                {/* Inbox list */}
                <div className="w-[280px] border-r border-gray-100 flex flex-col bg-gray-50/40" data-id="element-13539" data-mp="3tsq2qzr51xm7">
                  <div className="p-3 border-b border-gray-100" data-id="element-13540" data-mp="2k2jcpcgkkha">
                    <input type="text" placeholder="Search messages..." value={inboxSearch} onChange={e => setInboxSearch(e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] bg-white" data-id="element-13541" data-mp="3kzczb3vg7dlv" />
                  </div>
                  <div className="flex-1 overflow-y-auto" data-id="element-13542" data-mp="321k8gzy3mu4u">
                    {conversations.filter(c => c.customerName.toLowerCase().includes(inboxSearch.toLowerCase())).map(conv => {
                  const last = conv.messages[conv.messages.length - 1];
                  const isActive = selectedConversationId === conv.id;
                  return <button key={conv.id} onClick={() => setSelectedConversationId(conv.id)} className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-white transition-colors ${isActive ? 'bg-white border-l-2 border-l-[#E2231A]' : 'border-l-2 border-l-transparent'}`} data-id="element-13543" data-mp="28afod3avxrqf">
                            <div className="flex items-center justify-between mb-1" data-id="element-13544" data-mp="2ryoe76oyhlmr">
                              <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-13545" data-mp="2n3ramfag44h9">
                                {conv.customerName}
                              </span>
                              <span className="text-[10px] text-gray-400 whitespace-nowrap ml-2" data-id="element-13546" data-mp="3fnfzrf81j5qk">
                                {last.time}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-2" data-id="element-13547" data-mp="3i85kv1ehdgk3">
                              <p className="text-xs text-gray-500 truncate flex-1" data-id="element-13548" data-mp="3n5xoutsegt4n">
                                {last.sender === 'shop' ? 'You: ' : ''}
                                {last.text}
                              </p>
                              {conv.unread > 0 && <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-bold text-white bg-[#E2231A] rounded-full" data-id="element-13549" data-mp="4yztrgcb1yta">
                                  {conv.unread}
                                </span>}
                            </div>
                          </button>;
                })}
                    {conversations.length === 0 && <p className="text-xs text-gray-400 text-center py-8 px-4" data-id="element-13550" data-mp="sp3w7kdrbsip">
                        No conversations yet. Once customers reach out, they'll
                        appear here.
                      </p>}
                  </div>
                </div>

                {/* Thread */}
                <div className="flex-1 flex flex-col bg-white min-w-0" data-id="element-13551" data-mp="ospudwll4g4u">
                  {selectedConversationId ? (() => {
                const conv = conversations.find(c => c.id === selectedConversationId);
                if (!conv) return null;
                return <>
                          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3 shrink-0" data-id="element-13552" data-mp="62zxn3y9hkaa">
                            <div className="w-9 h-9 rounded-full bg-[#FEE7E5] text-[#9F140F] flex items-center justify-center font-semibold text-sm" data-id="element-13553" data-mp="123wgy5aoc8k">
                              {conv.customerName.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-13554" data-mp="2ufngewn7vu7a">
                              <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-13555" data-mp="2ue40j75h3l61">
                                {conv.customerName}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-13556" data-mp="2lg0qb6zxuatj">Customer</p>
                            </div>
                          </div>
                          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50/30" data-id="element-13557" data-mp="2qg6ay37six24">
                            {conv.messages.map(msg => {
                      const isShop = msg.sender === 'shop';
                      return <div key={msg.id} className={`flex ${isShop ? 'justify-end' : 'justify-start'}`} data-id="element-13558" data-mp="34d1xmygysn0n">
                                  <div className="max-w-[75%]" data-id="element-13559" data-mp="vtg6fq5fp6g">
                                    <div className={`rounded-2xl px-3.5 py-2 text-sm ${isShop ? 'bg-[#E2231A] text-white rounded-br-sm' : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'}`} data-id="element-13560" data-mp="5o36hwv6er99">
                                      {msg.text}
                                    </div>
                                    <p className={`text-[10px] text-gray-400 mt-1 ${isShop ? 'text-right' : 'text-left'}`} data-id="element-13561" data-mp="mt134hjori5">
                                      {msg.time}
                                    </p>
                                  </div>
                                </div>;
                    })}
                          </div>
                          <div className="p-3 border-t border-gray-100 shrink-0" data-id="element-13562" data-mp="3e30gaarl80kk">
                            <div className="flex items-end gap-2" data-id="element-13563" data-mp="3912ogs54fxg0">
                              <textarea value={newMessageText} onChange={e => setNewMessageText(e.target.value)} placeholder="Type a reply..." rows={1} onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (!newMessageText.trim()) return;
                          setConversations(prev => prev.map(c => c.id === selectedConversationId ? {
                            ...c,
                            unread: 0,
                            messages: [...c.messages, {
                              id: 'm' + Date.now(),
                              sender: 'shop',
                              text: newMessageText,
                              time: 'Just now'
                            }]
                          } : c));
                          setNewMessageText('');
                        }
                      }} className="flex-1 max-h-32 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] resize-none" data-id="element-13564" data-mp="3510tt2xt04a8" />
                              <button onClick={() => {
                        if (!newMessageText.trim()) return;
                        setConversations(prev => prev.map(c => c.id === selectedConversationId ? {
                          ...c,
                          unread: 0,
                          messages: [...c.messages, {
                            id: 'm' + Date.now(),
                            sender: 'shop',
                            text: newMessageText,
                            time: 'Just now'
                          }]
                        } : c));
                        setNewMessageText('');
                      }} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors" data-id="element-13565" data-mp="d72b5oh9ky28">
                                Send
                              </button>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1.5" data-id="element-13566" data-mp="14bepwy3lv0le">
                              Press Enter to send, Shift+Enter for a new line.
                            </p>
                          </div>
                        </>;
              })() : <div className="flex-1 flex flex-col items-center justify-center text-gray-400 px-6 text-center" data-id="element-13567" data-mp="ledlpfhvl4ii">
                      <MessageSquareIcon size={40} className="mb-3 opacity-30" data-id="element-13568" data-mp="qdyl1r0yjevr" />
                      <p className="text-sm font-medium text-gray-900" data-id="element-13569" data-mp="6tr8pnpki2c3">
                        No conversation selected
                      </p>
                      <p className="text-xs mt-1 text-gray-500 max-w-xs" data-id="element-13570" data-mp="fsuvp2ruv1vv">
                        Choose a customer from the list on the left to view the
                        thread and reply.
                      </p>
                    </div>}
                </div>
              </div>
            </div>}

          {/* FAQ */}
          {activeSection === 'faq' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13571" data-mp="zgu7mgy5of2v">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-13572" data-mp="uhhfbgoimfsw">
                <div className="flex items-center gap-2" data-id="element-13573" data-mp="18gh6ow3ex8sr">
                  <HelpCircleIcon size={16} className="text-gray-500" data-id="element-13574" data-mp="18exr77pkz1s1" />
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-13575" data-mp="13j8lemmywona">FAQ</h2>
                </div>
                <button onClick={addFaq} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-13576" data-mp="15suw1i9tcfa">
                  <PlusIcon size={14} data-id="element-13577" data-mp="a4ebvs83gjdy" />
                  Add question
                </button>
              </div>
              <div className="px-6 py-5 space-y-4" data-id="element-13578" data-mp="exhawfvn4l1j">
                {faqs.map((faq, i) => <div key={faq.id} className="border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-13579" data-mp="juhcoelgj1mu" data-mpi={i}>
                    <div className="flex items-start justify-between gap-2" data-id="element-13580" data-mp="tsnfqzj02dta">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13581" data-mp="xus5voa8osb6">
                        Question {i + 1}
                      </label>
                      <button onClick={() => removeFaq(faq.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" aria-label="Remove" data-id="element-13582" data-mp="2vmcvziw7zxtf">
                        <Trash2Icon size={14} data-id="element-13583" data-mp="2nbe200hbt8zb" />
                      </button>
                    </div>
                    <input type="text" value={faq.question} onChange={e => setFaqs(faqs.map(f => f.id === faq.id ? {
                ...f,
                question: e.target.value
              } : f))} placeholder="e.g. Do you ship internationally?" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13584" data-mp="2ic1dzcepxvv5" />
                    <textarea value={faq.answer} onChange={e => setFaqs(faqs.map(f => f.id === faq.id ? {
                ...f,
                answer: e.target.value
              } : f))} placeholder="Provide a clear, helpful answer..." rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13585" data-mp="39iq6x19jbksl" />
                  </div>)}
                {faqs.length === 0 && <p className="text-sm text-gray-500 text-center py-4" data-id="element-13586" data-mp="27w8xii16d82y">
                    No FAQs yet. Add your first question.
                  </p>}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13587" data-mp="24e1pbb65bvii">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13588" data-mp="20cg492jgkax8">
                  Save changes
                </button>
              </div>
            </div>}

          {/* About */}
          {activeSection === 'about' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13589" data-mp="1go7cgtltu6y9">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13590" data-mp="1vcv3qnon3uie">
                <InfoIcon size={16} className="text-gray-500" data-id="element-13591" data-mp="1qizafyjbmvtk" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13592" data-mp="12ug07dgpo8yu">About</h2>
              </div>
              <div className="px-6 py-5" data-id="element-13593" data-mp="xwejgz6rclqj">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13594" data-mp="xvd62o29rn5n">
                  About your shop
                </label>
                <textarea value={aboutText} onChange={e => setAboutText(e.target.value)} rows={6} placeholder="Tell customers about your shop, mission and what makes you unique..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13595" data-mp="32wfjtceow2bk" />
                <p className="text-xs text-gray-400 mt-1.5" data-id="element-13596" data-mp="2jz61xzp0iwwf">
                  Appears on your shop's About page and in storefront footer.
                </p>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13597" data-mp="2szb6m22tnh6w">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13598" data-mp="3aip2b9m219e">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Contact */}
          {activeSection === 'contact' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13599" data-mp="8m5zsfnpm80l">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13600" data-mp="4lvvd7fkfpnu">
                <MailIcon size={16} className="text-gray-500" data-id="element-13601" data-mp="3vrdssbgwyh19" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13602" data-mp="3vpkgognj74lv">
                  Contact details
                </h2>
              </div>
              <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13603" data-mp="3lsxyjzmlarnj">
                <div data-id="element-13604" data-mp="3hrvzbisoaflk">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13605" data-mp="3hsntqqaja7a9">
                    Contact email
                  </label>
                  <div className="relative" data-id="element-13606" data-mp="1g0j4xnobzq3r">
                    <MailIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-13607" data-mp="1g1u8pnhs0xuv" />
                    <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13608" data-mp="23bdxyes8ndnn" />
                  </div>
                </div>
                <div data-id="element-13609" data-mp="1kkwbut1wvgb9">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13610" data-mp="1kl612ef4tjfw">
                    Contact phone
                  </label>
                  <div className="relative" data-id="element-13611" data-mp="1bj6ihhogy7au">
                    <PhoneIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-13612" data-mp="1bk842g342c6t" />
                    <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13613" data-mp="35wo8rcg9di9k" />
                  </div>
                </div>
                <div className="md:col-span-2" data-id="element-13614" data-mp="3k26pss77tlhz">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13615" data-mp="3k1ettg9phlr6">
                    Address
                  </label>
                  <div className="relative" data-id="element-13616" data-mp="3ts9jkum5v3hd">
                    <MapPinIcon size={14} className="absolute left-3 top-3 text-gray-400" data-id="element-13617" data-mp="bnfx2cewtwcm" />
                    <textarea value={contactAddress} onChange={e => setContactAddress(e.target.value)} rows={2} className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13618" data-mp="7mckbys5sgfm" />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13619" data-mp="1deeh1thf7rm0">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13620" data-mp="18jiew0igznv6">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Social media */}
          {activeSection === 'social' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13621" data-mp="potoqjzdni02">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13622" data-mp="kph485vzan0n">
                <InstagramIcon size={16} className="text-gray-500" data-id="element-13623" data-mp="2jzyz074yysod" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13624" data-mp="2f2qew109gekh">
                  Social media
                </h2>
              </div>
              <div className="px-6 py-5 space-y-3" data-id="element-13625" data-mp="2nglcnodoomay">
                {[{
              icon: FacebookIcon,
              label: 'Facebook',
              value: facebook,
              set: setFacebook,
              color: 'text-[#1877F2]',
              type: 'url'
            }, {
              icon: InstagramIcon,
              label: 'Instagram',
              value: instagram,
              set: setInstagram,
              color: 'text-[#E4405F]',
              type: 'url'
            }, {
              icon: MessageCircleIcon,
              label: 'WhatsApp',
              value: whatsapp,
              set: setWhatsapp,
              color: 'text-[#25D366]',
              type: 'tel'
            }, {
              icon: () => <span className="flex items-center justify-center w-full h-full font-bold text-white bg-black rounded-lg" data-id="element-13626" data-mp="349kwldek4l3i">
                        𝕏
                      </span>,
              label: 'X.com',
              value: twitter,
              set: setTwitter,
              color: '',
              type: 'url'
            }, {
              icon: MusicIcon,
              label: 'TikTok',
              value: tiktok,
              set: setTiktok,
              color: 'text-gray-900',
              type: 'url'
            }].map(s => {
              const Icon = s.icon;
              return <div key={s.label} className="flex items-center gap-3" data-id="element-13627" data-mp="22tn4wpxou53u">
                      <div className={`w-9 h-9 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 ${s.color}`} data-id="element-13628" data-mp="22riq3cy93los">
                        <Icon size={16} data-id="element-13629" data-mp="daesnppna0lv" />
                      </div>
                      <div className="flex-1" data-id="element-13630" data-mp="i3zvpw63hkrx">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-13631" data-mp="3ebt8rm6gg2m">
                          {s.label}
                        </label>
                        <input type={s.type} value={s.value} onChange={e => s.set(e.target.value)} placeholder={s.type === 'tel' ? '+960 123 4567' : `https://${s.label.toLowerCase().replace(/[^a-z]/g, '')}.com/your-handle`} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13632" data-mp="8dy5oyu6lsad" />
                      </div>
                    </div>;
            })}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13633" data-mp="3s32xfmi9nk1f">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13634" data-mp="3n8qdlbcvo6p5">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Widgets */}
          {activeSection === 'widgets' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13635" data-mp="9tu5oevhonil">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13636" data-mp="4yn4bdpozuwu">
                <SparklesIcon size={16} className="text-gray-500" data-id="element-13637" data-mp="x1kt9inkido" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-13638" data-mp="2pnugocd54696">
                  Storefront widgets
                </h2>
              </div>
              <div className="px-6 py-5 space-y-3" data-id="element-13639" data-mp="2lnk8ct1vl3r5">
                <p className="text-sm text-gray-500 -mt-1 mb-2" data-id="element-13640" data-mp="114gg3sty8c45">
                  Choose which trust-building widgets appear on your product
                  pages and checkout to reassure shoppers.
                </p>
                {[{
              label: 'Free shipping widget',
              desc: 'Highlight free shipping eligibility on product pages.',
              value: showFreeShippingWidget,
              set: setShowFreeShippingWidget
            }, {
              label: 'Easy returns widget',
              desc: 'Show your returns policy as a reassurance badge.',
              value: showEasyReturnsWidget,
              set: setShowEasyReturnsWidget
            }, {
              label: 'Secure checkout widget',
              desc: 'Display SSL/payment security icons at checkout.',
              value: showSecureCheckoutWidget,
              set: setShowSecureCheckoutWidget
            }, {
              label: 'Local shop widget',
              desc: 'Show a "Locally owned & operated in Malaysia" badge.',
              value: showLocalShopWidget,
              set: setShowLocalShopWidget
            }].map(w => <div key={w.label} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3" data-id="element-13641" data-mp="1powp66fazk1y">
                    <div data-id="element-13642" data-mp="1yhgshk6jict4">
                      <p className="text-sm font-medium text-gray-900" data-id="element-13643" data-mp="1yhqc8q0zrnqd">
                        {w.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-13644" data-mp="1ugeqivl8nmng">{w.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-13645" data-mp="28fxpoyw2nnnm">
                      <input type="checkbox" className="sr-only peer" checked={w.value} onChange={e => w.set(e.target.checked)} data-id="element-13646" data-mp="23i4jw6yokvnv" />
                      <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-13647" data-mp="2d4firbtnv169"></div>
                    </label>
                  </div>)}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13648" data-mp="293wozc87jl1f">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13649" data-mp="1ufip9a638cp3">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Legal */}
          {activeSection === 'legal' && <div className="space-y-6" data-id="element-13650" data-mp="25tlre2x0zlf9">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13651" data-mp="20v0x0k4gfvyq">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13652" data-mp="1vvxt5307nvsc">
                  <ScrollTextIcon size={16} className="text-gray-500" data-id="element-13653" data-mp="1vxr2wr64ew4k" />
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-13654" data-mp="1qwbjt4ni6zot">
                    Privacy policy
                  </h2>
                </div>
                <div className="px-6 py-5" data-id="element-13655" data-mp="1i3zr1c4x305o">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13656" data-mp="1i2yhjc3julq2">
                    Privacy policy text
                  </label>
                  <textarea value={privacyPolicy} onChange={e => setPrivacyPolicy(e.target.value)} rows={10} placeholder="Explain how you collect, use, and protect customer data..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13657" data-mp="187emerwt7r2d" />
                  <p className="text-xs text-gray-400 mt-1.5" data-id="element-13658" data-mp="3essu0o21u863">
                    Shown on your shop's Privacy Policy page and linked from the
                    storefront footer.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13659" data-mp="2r5jhus6akti0">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-13660" data-mp="2w3urb1sod1zp">
                  <ScrollTextIcon size={16} className="text-gray-500" data-id="element-13661" data-mp="313h5pek8ooa3" />
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-13662" data-mp="3114gqhg2ddnj">
                    Terms of service
                  </h2>
                </div>
                <div className="px-6 py-5" data-id="element-13663" data-mp="28did596u690o">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-13664" data-mp="28cfmn2dfe8la">
                    Terms of service text
                  </label>
                  <textarea value={termsOfService} onChange={e => setTermsOfService(e.target.value)} rows={10} placeholder="Outline the terms customers agree to when buying from your shop..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13665" data-mp="2lvas296d0cb6" />
                  <p className="text-xs text-gray-400 mt-1.5" data-id="element-13666" data-mp="1y7jez3czvv8u">
                    Shown on your shop's Terms page and linked from checkout.
                  </p>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-13667" data-mp="2c0iw19sfdxii">
                  <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-13668" data-mp="2764s2izp6i28">
                    Save changes
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}